<template>
  <button
    type="button"
    :disabled="disabled || loading"
    :class="buttonClasses"
    @click="$emit('click')"
  >
    <!-- Avatar or Loading Spinner -->
    <div :class="avatarContainerClasses">
      <template v-if="loading">
        <ArrowPathIcon :class="spinnerClasses" />
      </template>
      <template v-else>
        <img
          src="/img/righty-light.jpg"
          alt="Righty"
          class="h-full w-full rounded-full object-cover dark:hidden"
        >
        <img
          src="/img/righty-dark.jpg"
          alt="Righty"
          class="hidden h-full w-full rounded-full object-cover dark:block"
        >
      </template>
    </div>
    <span>{{ label }}</span>
  </button>
</template>

<script setup lang="ts">
import { ArrowPathIcon } from '@heroicons/vue/24/outline'

type ColorVariant = 'purple' | 'blue' | 'emerald' | 'amber' | 'rose'

interface Props {
  label?: string
  loading?: boolean
  disabled?: boolean
  size?: 'small' | 'default'
  color?: ColorVariant
}

const props = withDefaults(defineProps<Props>(), {
  label: 'AI Generate',
  loading: false,
  disabled: false,
  size: 'default',
  color: 'purple'
})

defineEmits<{
  click: []
}>()

// Color variant styles
const colorStyles: Record<ColorVariant, { gradient: string; hoverGradient: string; text: string; border: string; spinner: string }> = {
  purple: {
    gradient: 'from-purple-500/10 to-pink-500/10',
    hoverGradient: 'hover:from-purple-500/20 hover:to-pink-500/20',
    text: 'text-purple-700 dark:text-purple-300',
    border: 'border-purple-200 dark:border-purple-800',
    spinner: 'text-purple-500 dark:text-purple-400'
  },
  blue: {
    gradient: 'from-blue-500/10 to-cyan-500/10',
    hoverGradient: 'hover:from-blue-500/20 hover:to-cyan-500/20',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border-blue-200 dark:border-blue-800',
    spinner: 'text-blue-500 dark:text-blue-400'
  },
  emerald: {
    gradient: 'from-emerald-500/10 to-teal-500/10',
    hoverGradient: 'hover:from-emerald-500/20 hover:to-teal-500/20',
    text: 'text-emerald-700 dark:text-emerald-300',
    border: 'border-emerald-200 dark:border-emerald-800',
    spinner: 'text-emerald-500 dark:text-emerald-400'
  },
  amber: {
    gradient: 'from-amber-500/10 to-orange-500/10',
    hoverGradient: 'hover:from-amber-500/20 hover:to-orange-500/20',
    text: 'text-amber-700 dark:text-amber-300',
    border: 'border-amber-200 dark:border-amber-800',
    spinner: 'text-amber-500 dark:text-amber-400'
  },
  rose: {
    gradient: 'from-rose-500/10 to-pink-500/10',
    hoverGradient: 'hover:from-rose-500/20 hover:to-pink-500/20',
    text: 'text-rose-700 dark:text-rose-300',
    border: 'border-rose-200 dark:border-rose-800',
    spinner: 'text-rose-500 dark:text-rose-400'
  }
}

const currentColorStyle = computed(() => colorStyles[props.color])

const buttonClasses = computed(() => [
  'inline-flex items-center gap-2 rounded-lg font-medium',
  'bg-gradient-to-r',
  currentColorStyle.value.gradient,
  currentColorStyle.value.text,
  currentColorStyle.value.hoverGradient,
  'border',
  currentColorStyle.value.border,
  'transition-all duration-200',
  'disabled:opacity-50 disabled:cursor-not-allowed',
  props.size === 'small' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm'
])

const avatarContainerClasses = computed(() => [
  'flex-shrink-0 overflow-hidden rounded-full flex items-center justify-center',
  props.loading
    ? 'bg-transparent'
    : 'bg-slate-200 dark:bg-slate-700',
  props.size === 'small' ? 'h-5 w-5' : 'h-6 w-6'
])

const spinnerClasses = computed(() => [
  'animate-spin',
  currentColorStyle.value.spinner,
  props.size === 'small' ? 'h-4 w-4' : 'h-5 w-5'
])
</script>
